<template>
  <BlogPostLayout>
    <template #image>
      <BlogHeader
        :title="postData.title"
        :excerpt="postData.excerpt"
        :image="postData.image"
        :author="postData.author"
        :date="postData.date"
        :updatedAt="postData.updatedAt"
        :categories="postData.categories"
        :readTime="postData.readTime"
      />
    </template>
    <template #content>
      <article class="blog-content" lang="es">
        <section class="intro">
          <p>
            Integrar un tour virtual 360° en tu sitio web transforma a los
            visitantes pasivos en exploradores activos de tu espacio. Las
            páginas con un tour virtual integrado ven cómo la duración media de
            sesión pasa de menos de 2 minutos a más de 5 — una señal muy
            positiva para el posicionamiento en Google. Esta guía cubre todos
            los métodos de integración y las principales plataformas.
          </p>
        </section>

        <section>
          <h2>Cómo funciona la integración de un tour virtual</h2>

          <h3>El método iFrame</h3>
          <p>
            El método estándar consiste en usar una etiqueta HTML iFrame. El
            iFrame carga el contenido del tour (alojado en Travvir) dentro de
            una ventana en tu página. Estructura básica:
          </p>
          <pre><code>&lt;iframe
  src="https://app.travvir.com/tour/TU-ID-TOUR"
  width="100%"
  height="500"
  frameborder="0"
  allowfullscreen
  allow="xr-spatial-tracking; gyroscope; accelerometer"
&gt;&lt;/iframe&gt;</code></pre>
          <p>
            El atributo <code>allowfullscreen</code> activa el modo pantalla
            completa. El atributo <code>allow</code> concede los permisos
            necesarios para el giroscopio en móvil — imprescindible para una
            buena experiencia móvil.
          </p>

          <h3>Obtener el código de integración desde Travvir</h3>
          <p>
            En tu panel de Travvir: selecciona tu tour → haz clic en Compartir →
            selecciona Integrar → copia el código iFrame. El código Travvir ya
            incluye los permisos correctos y las proporciones preconfiguradas.
          </p>
        </section>

        <section>
          <h2>Integración por plataforma</h2>

          <h3>WordPress</h3>
          <p>
            En el editor Gutenberg: añade un bloque "HTML personalizado" donde
            quieras el tour, pega el código iFrame y publica. No uses el modo
            "HTML" de un bloque párrafo — elimina las etiquetas iFrame. Debes
            usar el bloque HTML dedicado.
          </p>

          <h3>Squarespace</h3>
          <p>
            Añade un bloque Embed a tu página, cambia a la opción "Código de
            integración" (no la integración por URL) y pega el código iFrame.
            Squarespace conserva los iFrames y los muestra correctamente.
          </p>

          <h3>Wix</h3>
          <p>
            En el editor Wix: Añadir → Integrar → HTML iFrame → pega tu código.
            Redimensiona el componente en el lienzo para ajustar las
            dimensiones.
          </p>

          <h3>Webflow</h3>
          <p>
            Añade un elemento Embed en tu lienzo de Webflow, pega el código
            iFrame en el campo HTML. Webflow renderiza los iFrames de forma
            nativa con interactividad completa, incluido el giroscopio en móvil.
          </p>
        </section>

        <section>
          <h2>Optimizar la integración para el rendimiento</h2>

          <h3>Carga diferida (lazy loading)</h3>
          <p>
            Añade <code>loading="lazy"</code> a tu etiqueta iFrame para que el
            tour solo se cargue cuando el usuario haga scroll hasta él. Esto
            mejora notablemente el tiempo de carga inicial de la página.
          </p>

          <h3>Altura mínima recomendada</h3>
          <p>
            Define una altura mínima de 500 px para una navegación cómoda en
            escritorio. En móvil, usa un ratio responsive con CSS (ej.:
            <code>padding-bottom: 56.25%</code>) para que el tour se adapte
            automáticamente a la pantalla.
          </p>
        </section>

        <section>
          <h2>Preguntas frecuentes</h2>

          <h3>¿La integración ralentiza mi web?</h3>
          <p>
            No si usas lazy loading. El tour virtual solo se carga cuando el
            visitante hace scroll hasta él, lo que preserva la velocidad de
            carga inicial.
          </p>

          <h3>¿Puedo integrar varios tours en la misma página?</h3>
          <p>
            Técnicamente sí, pero no es recomendable. Opta por un tour por
            página, o usa pestañas para cargar un solo tour a la vez.
          </p>
        </section>
        <section class="continue-reading">
          <h2>Seguir Leyendo</h2>
          <ul>
          <li><a href="/blog/es/tour-virtual-wordpress">Cómo Añadir un Tour Virtual en WordPress: Guía Completa 2026</a></li>
          <li><a href="/blog/es/guia-tour-virtual-smartphone">Guía Completa: Crear un Tour Virtual 360° con Smartphone 2026</a></li>
          <li><a href="/blog/es/guia-seo-tour-virtual">Guía SEO para Tours Virtuales: Cómo Posicionarlos en Google 2026</a></li>
          <li><a href="/blog/es/analitica-tour-virtual">Analítica de Tours Virtuales: Métricas Clave y Optimización</a></li>
          <li><a href="/blog/es/compartir-foto-360-redes-sociales">Cómo Compartir Fotos 360° en Redes Sociales 2026</a></li>
          <li><a href="/blog/es/tour-virtual-360-guia-completa">Tour Virtual 360°: Guía Completa para Principiantes (2026)</a></li>
          </ul>
        </section>



        <RelatedPosts
          :currentSlug="postData.slug"
          :currentCategories="postData.categories"
        />
      </article>
    </template>
  </BlogPostLayout>
</template>

<script setup>
import blogPostsData from "~/data/blogPosts.json";

const postData = blogPostsData.find(
  (p) => p.slug === "integrar-tour-virtual-sitio-web",
);

useBlogSeo({
  title: "Cómo Integrar un Tour Virtual 360° en tu Sitio Web 2026 | Travvir",
  description:
    "Guía completa para integrar un tour virtual 360° en tu web: método iFrame, WordPress, Squarespace, Wix, Webflow y optimización de rendimiento.",
  keywords:
    "integrar tour virtual sitio web, código integración tour virtual, iframe tour virtual, embed tour virtual wordpress",
  slug: "integrar-tour-virtual-sitio-web",
  image: postData?.image,
  postData,
  lang: "es",
  faqItems: [
    {
      question: "¿La integración ralentiza mi web?",
      answer:
        "No si usas lazy loading — el tour solo se carga cuando el visitante hace scroll hasta él.",
    },
    {
      question: "¿Puedo integrar varios tours en la misma página?",
      answer:
        "Técnicamente sí, pero no recomendable. Usa pestañas para cargar un solo tour a la vez.",
    },
  ],
});

</script>
